import React from 'react';

import { Typography } from 'components/shared/Typography';
import { Icon } from 'components/shared/Icon';

import { Tooltip } from 'components/Tooltip';
import { FormikField } from 'components/FormikField';

import { PropertySelectField } from '../PropertySelectField';

import { getPropertyIcon, getPropertyLabel } from './TextIconProperty.helpers';

import styles from './TextIconProperty.module.scss';

type LayerProp = {
  key?: string;
  value?: unknown;
};

const _renderField = ({
  namePrefix,
  readOnly,
  property,
  onChanged,
}: {
  namePrefix?: string;
  readOnly?: boolean;
  property: LayerProp;
  onChanged?: () => void;
}) => (
  <FormikField
    name={namePrefix ?? ''}
    withControl={false}
    component={PropertySelectField}
    componentProps={{
      type: 'checkbox',
      readOnly,
      property,
      renderProperty: _renderContent,
      onChanged,
    }}
  />
);

const _renderContent = ({ key, value }: LayerProp) => (
  <>
    <Typography
      className={styles.value}
      variant="body2"
      weight="medium"
      mode="compact"
    >
      {getPropertyLabel({ type: key })}
    </Typography>
    <Icon
      className={styles.icon}
      name={getPropertyIcon({
        type: key,
        value,
      })}
    />
  </>
);

type TextIconPropertyProps = {
  isInitialState?: boolean;
  readOnly?: boolean;
  tooltip?: React.ReactNode;
  designType?: string;
  namePrefix?: string;
  property?: LayerProp | null;
  onChanged?: () => void;
};

const TextIconProperty = ({
  isInitialState,
  readOnly,
  tooltip,
  designType,
  namePrefix,
  property,
  onChanged,
}: TextIconPropertyProps) => {
  if (!property) {
    return null;
  }

  if (isInitialState || designType === 'PROTOTYPE') {
    if (tooltip) {
      return (
        <Tooltip
          text={tooltip}
          triggerElement={
            <div className={styles.propertyReadOnly}>
              {_renderContent(property)}
            </div>
          }
        />
      );
    }

    return (
      <div className={styles.propertyReadOnly}>{_renderContent(property)}</div>
    );
  }

  if (tooltip) {
    return (
      <Tooltip
        text={tooltip}
        triggerElement={_renderField({
          namePrefix,
          readOnly,
          property,
          onChanged,
        })}
      />
    );
  }

  return _renderField({
    namePrefix,
    readOnly,
    property,
    onChanged,
  });
};

export default React.memo(TextIconProperty);
